import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/shared/cn';
import {
  issueListHiddenCount,
  issueListVisibleRows,
  issueRowFigureView,
  type IssueListRow,
} from './IssueList.transform';
import { NEUTRAL_CHIP, statePill } from './SeniorMetTabs';

const ACTION_LINK_CLASS =
  'border-b border-[var(--hairline-strong)] text-[12.5px] font-semibold text-ink-primary hover:border-ink-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]';

/** Paul's shared `renderIssueList(roleKey)` (map-role-dashboards.md §2 C5) as
 * the Senior metallurgist landing's ranked list (polish.P27, locked comp
 * `landing-senior-met.html` A): rank, title and driver path, tier and area,
 * then the figure column — unit, amount, its note and a neutral magnitude bar.
 * A row opens in place to its action and context. */
export function IssueList({
  rows,
  roleLabel,
  onOpenCase,
}: {
  rows: readonly IssueListRow[];
  roleLabel: string;
  onOpenCase?: (caseId: string) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const visibleRows = issueListVisibleRows(rows, showAll);
  const hiddenCount = issueListHiddenCount(rows);

  return (
    <div data-issue-list>
      <ul>
        {visibleRows.map((row) => {
          const isOpen = expanded === row.id;
          const { parts, tierState, oneOff, note } = issueRowFigureView(row);
          return (
            <li key={row.id} className="border-b border-[var(--color-divider)] last:border-b-0" data-issue-row={row.id}>
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setExpanded(isOpen ? null : row.id)}
                className="grid w-full grid-cols-[24px_minmax(0,1fr)_236px_16px] items-start gap-x-3.5 gap-y-1 py-2.5 text-left tabular-nums hover:bg-[color-mix(in_srgb,var(--surface-1)_60%,transparent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-500)]"
              >
                <span className={cn('pt-px text-center text-xs font-bold', row.rank === 1 ? 'text-[var(--color-accent-500)]' : 'text-muted')}>
                  {row.rank}
                </span>
                <span className="min-w-0">
                  <span className="block text-[13px] font-semibold text-ink-primary">{row.title}</span>
                  <span className="mt-0.5 block text-[11.5px] text-muted">{row.driverTreePathHint}</span>
                  <span className="mt-[5px] flex flex-wrap items-center gap-1.5">
                    <span className={statePill(tierState)}>{row.tier}</span>
                    <span className={NEUTRAL_CHIP}>{row.area}</span>
                  </span>
                </span>
                <span className="flex min-w-0 flex-col items-end text-right">
                  <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted">
                    {row.impactUnitLabel} · {row.impType}
                  </span>
                  <span className={cn('text-[15px] font-semibold', row.impactMuted ? 'text-muted' : 'text-ink-primary')}>
                    {parts.map((part, index) => (
                      <Fragment key={part}>
                        {index > 0 ? <span className="text-xs font-normal text-muted"> vs </span> : null}
                        <span className="whitespace-nowrap">{part}</span>
                      </Fragment>
                    ))}
                  </span>
                  {note ? <span className="max-w-[210px] text-[11.5px] leading-snug text-muted">{note}</span> : null}
                  {oneOff ? null : (
                    <span className="mt-[5px] h-1 w-full overflow-hidden rounded-full bg-[var(--hairline-strong)]" aria-hidden="true">
                      <span
                        className={cn('block h-full rounded-full', row.impactMuted ? 'bg-[var(--ink-tertiary)] opacity-60' : 'bg-[var(--ink-secondary)]')}
                        style={{ width: `${row.impactBarPct}%` }}
                      />
                    </span>
                  )}
                </span>
                {isOpen ? (
                  <ChevronUp className="mt-0.5 h-3.5 w-3.5 text-muted" aria-hidden="true" />
                ) : (
                  <ChevronDown className="mt-0.5 h-3.5 w-3.5 text-muted" aria-hidden="true" />
                )}
              </button>
              {row.ownerBreakdown.length > 0 ? (
                <p className="-mt-1 flex flex-wrap gap-x-3.5 gap-y-1 pb-2.5 pl-[38px] text-xs tabular-nums text-ink-secondary">
                  {row.ownerBreakdown.map((share) => (
                    <Link key={share.issueId} to={share.href} className="hover:underline">
                      {share.label} <span className="font-semibold text-ink-primary">{share.amountLabel}</span>
                    </Link>
                  ))}
                </p>
              ) : null}
              {isOpen ? (
                <div className="mb-2.5 ml-[38px] mr-[30px] rounded-xl border border-[var(--hairline)] bg-[var(--surface-2)] px-3 py-2 text-[12.5px]">
                  <p className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted">
                    {roleLabel} — action and context
                  </p>
                  <p className="mt-1 text-ink-primary">{row.body}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2.5">
                    {row.caseId && onOpenCase ? (
                      <button type="button" onClick={() => onOpenCase(row.caseId!)} className={ACTION_LINK_CLASS}>
                        Open case →
                      </button>
                    ) : null}
                    <Link to={row.driverTreeHref} className={ACTION_LINK_CLASS}>
                      Driver tree →
                    </Link>
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      {!showAll && hiddenCount > 0 ? (
        <button type="button" onClick={() => setShowAll(true)} className={cn(ACTION_LINK_CLASS, 'mt-2 inline-block')}>
          Show {hiddenCount} more case{hiddenCount === 1 ? '' : 's'} →
        </button>
      ) : null}
    </div>
  );
}
